import type { ReactNode } from 'react';
import type { IPermission } from '@condo/domain';
import { useHasPermissions } from '../model/useHasPermissions.hook';

export interface RequirePermissionsProps {
    permissions: IPermission[];
    children: ReactNode;
    /** Rendered while current-user permissions are loading. */
    loadingFallback?: ReactNode;
    /**
     * Rendered when the current user does not satisfy ALL `permissions`.
     * Defaults to `null` so feature blocks are silently hidden.
     */
    fallback?: ReactNode;
}

/**
 * Generic permission gate for feature blocks.
 *
 * - ALL-of semantics: user must have every permission in `permissions`.
 * - Admin bypass is handled by `useHasPermissions` (returns true for admins).
 * - Permission denial is deferred until current-user details finish loading.
 * - When denied, renders `fallback` (defaults to `null` → silent hide).
 */
export function RequirePermissions({ permissions, children, loadingFallback = null, fallback = null }: RequirePermissionsProps) {
    const { hasPermission, isLoading } = useHasPermissions();

    if (isLoading) {
        return <>{loadingFallback}</>;
    }

    if (permissions.length === 0 || hasPermission(...permissions)) {
        return <>{children}</>;
    }

    return <>{fallback}</>;
}
